import { useEffect, useMemo, useRef, useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  spacesListQueryOptions,
  createSpace,
  invalidateSpaceScopedQueries,
} from "@/modules/spaces/spaces-queries";
import {
  type SessionVisibility,
  type AccessMode,
  type SpaceSummary,
  deriveUiType,
  UI_TYPE_LABEL,
  type SpaceUiType,
} from "@/modules/spaces/types";
import { useSpaceStore } from "@/modules/spaces/space-store";
import { useAuth } from "@/shared/hooks/use-auth";
import { SpaceAccessDialog } from "@/modules/spaces/SpaceAccessDialog";
import { SpaceTypeRadios, InviteLinkRow } from "@/modules/spaces/SpaceFormParts";

/**
 * Пространства — каталог реальных пространств пользователя.
 *
 * Click on a card → switch active space (X-Space-Id flip) and refetch
 * every space-scoped query. Cog → access management dialog.
 */

type Tab = "all" | "personal" | "team" | "public";

const spacesRoute = getRouteApi("/_app/spaces/");

export function SpacesView() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = spacesRoute.useSearch();
  const currentSpaceId = useSpaceStore((s) => s.currentSpaceId);
  const setCurrentSpaceId = useSpaceStore((s) => s.setCurrentSpaceId);

  const { data: spaces, isLoading } = useQuery(spacesListQueryOptions());
  const [tab, setTab] = useState<Tab>("all");
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // ?new=1 → открыть диалог создания. Сбрасываем параметр сразу после
  // открытия, чтобы reload страницы не открывал модалку повторно.
  useEffect(() => {
    if (search.new) {
      setCreateOpen(true);
      navigate({ to: "/spaces", search: {}, replace: true });
    }
  }, [search.new, navigate]);

  const filtered = useMemo(() => {
    const list = spaces ?? [];
    return list.filter((s) => {
      const ui = deriveUiType(s);
      if (tab === "personal" && ui !== "personal") return false;
      if (tab === "team" && ui !== "team_private" && ui !== "team_shared") return false;
      if (tab === "public" && ui !== "public") return false;
      if (query && !s.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [spaces, tab, query]);

  const onPick = (id: string) => {
    if (id !== currentSpaceId) {
      setCurrentSpaceId(id);
      invalidateSpaceScopedQueries(qc);
    }
    navigate({ to: "/spaces/$id", params: { id } });
  };

  return (
    <div className="cmd-page">
      <header className="cmd-page-header">
        <h1 className="cmd-page-title">Пространства</h1>
        <button
          type="button"
          className="cmd-page-create"
          onClick={() => setCreateOpen(true)}
        >
          <span aria-hidden="true">+</span>
          <span>Создать пространство</span>
        </button>
      </header>

      <div className="cmd-page-toolbar">
        <div className="history-tabs" role="tablist">
            <button
              type="button"
              className={`history-tab${tab === "all" ? " active" : ""}`}
              onClick={() => setTab("all")}
            >
              Все
            </button>
            <button
              type="button"
              className={`history-tab${tab === "personal" ? " active" : ""}`}
              onClick={() => setTab("personal")}
            >
              Личные
            </button>
            <button
              type="button"
              className={`history-tab${tab === "team" ? " active" : ""}`}
              onClick={() => setTab("team")}
            >
              Командные
            </button>
            <button
              type="button"
              className={`history-tab${tab === "public" ? " active" : ""}`}
              onClick={() => setTab("public")}
            >
              Публичные
            </button>
          </div>

        <div className="cmd-search">
          <SearchIcon />
          <input
            type="search"
            className="cmd-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Название пространства"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : filtered.length === 0 ? (
        <div className="cmd-empty">
          {query ? (
            <p>Ничего не найдено по запросу «{query}»</p>
          ) : spaces && spaces.length === 0 ? (
            <>
              <p className="cmd-empty-title">Пространств пока нет</p>
              <p className="cmd-empty-hint">
                Нажмите «Создать пространство», чтобы добавить первое.
              </p>
            </>
          ) : (
            <p>Ничего не найдено</p>
          )}
        </div>
      ) : (
        <div className="cmd-grid">
          {filtered.map((s) => (
            <SpaceCard
              key={s.id}
              space={s}
              menuOpen={openMenu === s.id}
              onMenuToggle={() => setOpenMenu((m) => (m === s.id ? null : s.id))}
              onMenuClose={() => setOpenMenu(null)}
              onPick={() => onPick(s.id)}
              onOpenAccess={() => setAccessTarget(s.id)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreateSpaceDialog
          isAdmin={user?.role === "admin"}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            setCurrentSpaceId(id);
            invalidateSpaceScopedQueries(qc);
            navigate({ to: "/spaces/$id", params: { id } });
          }}
        />
      )}

      {accessTarget && (
        <SpaceAccessDialog
          spaceId={accessTarget}
          onClose={() => setAccessTarget(null)}
        />
      )}
    </div>
  );
}

function SpaceCard({
  space,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  onPick,
  onOpenAccess,
}: {
  space: SpaceSummary;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  onPick: () => void;
  onOpenAccess: () => void;
}) {
  const ui = deriveUiType(space);
  const wrapRef = useRef<HTMLDivElement>(null);
  const meta = `${UI_TYPE_LABEL[ui]} · ${space.member_count} ${plural(space.member_count, "участник", "участника", "участников")}`;

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) onMenuClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onMenuClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen, onMenuClose]);

  // Card-level click switches/opens the space; the kebab + its menu live
  // inside and stop propagation so they don't double-fire.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick();
        }
      }}
      className="cmd-card spaces-card"
    >
      <div className="cmd-card-head">
        <div className="cmd-card-head-main">
          <span className="spaces-card-icon" aria-hidden="true">
            <FolderIcon />
          </span>
          <span className="cmd-card-name">{space.name}</span>
        </div>
        {space.role === "owner" && (
          <div
            className="cmd-card-menu-wrap"
            ref={wrapRef}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="cmd-card-kebab"
              onClick={onMenuToggle}
              aria-label="Действия"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <KebabIcon />
            </button>
            {menuOpen && (
              <div className="cmd-card-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="cmd-card-menu-item"
                  onClick={() => {
                    onMenuClose();
                    onOpenAccess();
                  }}
                >
                  <CogIcon />
                  <span>Настройки</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <p className="cmd-card-desc">
        {space.description?.trim() || meta}
      </p>
      {space.description?.trim() && (
        <p className="spaces-card-meta">{meta}</p>
      )}
    </div>
  );
}

/**
 * Two-step create flow. Step 1 collects the basics (name, description,
 * type). On success we either:
 *   • personal / public → straight to the new space (no link to share —
 *     personal has no one to invite; public is auto-discoverable);
 *   • team_private / team_shared → step 2 surfaces the invite link so
 *     the owner can copy it before navigating away. The link is rendered
 *     by the shared `<InviteLinkRow>` so create and edit dialogs stay in
 *     lock-step.
 *
 * Cache invalidation for the new space happens immediately on creation
 * (so the catalog list refreshes behind the modal); the parent's
 * `onCreated` is only called once we're truly done with the dialog.
 */
function CreateSpaceDialog({
  isAdmin,
  onClose,
  onCreated,
}: {
  isAdmin: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<SpaceUiType>("personal");
  const [error, setError] = useState<string | null>(null);
  const [createdSpace, setCreatedSpace] = useState<SpaceSummary | null>(null);

  const skipsInviteStep = (t: SpaceUiType) => t === "personal" || t === "public";

  const mutation = useMutation({
    mutationFn: () => {
      // Public spaces share infra (agents, connectors, skills) but each
      // person's chats stay private — `organization + private`. Only
      // 'team_shared' has visibility=shared.
      const visibility: SessionVisibility =
        type === "team_shared" ? "shared" : "private";
      const access: AccessMode = type === "public" ? "organization" : "invite_only";
      return createSpace({
        name: name.trim(),
        description,
        session_visibility: visibility,
        access_mode: access,
      });
    },
    onSuccess: (sp) => {
      // Refresh the catalog behind the modal so the new card appears
      // even if the user lingers on step 2.
      qc.invalidateQueries({ queryKey: ["spaces"] });
      if (skipsInviteStep(type)) {
        onCreated(sp.id);
      } else {
        setCreatedSpace(sp);
      }
    },
    onError: (e: Error) => setError(e.message),
  });

  const finishStep2 = () => {
    if (createdSpace) onCreated(createdSpace.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-black/90">
            {createdSpace ? "Пространство создано" : "Новое пространство"}
          </h2>
          <button
            onClick={createdSpace ? finishStep2 : onClose}
            aria-label="Закрыть"
            className="rounded-full p-1 hover:bg-black/5"
          >
            <CloseIcon />
          </button>
        </header>

        {createdSpace ? (
          <>
            <p className="mb-4 text-sm text-black/60">
              Поделитесь ссылкой с коллегами, чтобы пригласить их в «{createdSpace.name}».
              Сейчас в пространстве только вы.
            </p>
            <div className="mb-2 text-sm text-black/70 font-medium">Ссылка для приглашения</div>
            <div className="mb-6">
              <InviteLinkRow spaceId={createdSpace.id} autoCreate showRevoke={false} />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={finishStep2}
                className="rounded-full bg-black px-5 py-2 text-sm text-white hover:bg-black/85"
              >
                Готово
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="block text-sm text-black/70 mb-1">Название</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например, Продажи"
              className="mb-4 w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm focus:outline-none focus:border-black/30"
            />
            <label className="block text-sm text-black/70 mb-1">Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Что за проект, для чего нужно"
              className="mb-4 w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm focus:outline-none focus:border-black/30"
            />

            <div className="mb-4 text-sm text-black/70 font-medium">Тип пространства</div>
            <div className="mb-5">
              <SpaceTypeRadios value={type} onChange={setType} isAdmin={isAdmin} />
            </div>

            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full px-4 py-2 text-sm text-black/70 hover:bg-black/5"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => mutation.mutate()}
                disabled={!name.trim() || mutation.isPending}
                className="rounded-full bg-black px-5 py-2 text-sm text-white hover:bg-black/85 disabled:bg-black/30"
              >
                {mutation.isPending ? "Создание…" : "Создать"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

function FolderIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-black/50">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
  );
}

function CogIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}
